// src/components/PreconditionsMapEditor.jsx
import React from "react";
import {
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

export default function PreconditionsMapEditor({
  value,              // object map: { [code]: integer }
  onChange,           // fn(nextMap)
  allowedKeys = [],   // array of strings
  title = "Preconditions (map of room code → integer)",
  size = "small",
}) {
  const [addKey, setAddKey] = React.useState("");
  const [addVal, setAddVal] = React.useState("");

  const usedKeys = React.useMemo(() => new Set(Object.keys(value || {})), [value]);
  const availableKeys = React.useMemo(
    () => allowedKeys.filter((k) => !usedKeys.has(k)),
    [allowedKeys, usedKeys]
  );

  const keySelectRef = React.useRef(null); // focus back here after add

  const isAddValid = React.useMemo(
    () => !!addKey && Number.isInteger(Number(addVal)),
    [addKey, addVal]
  );

  const handleValueChange = (code, nextStr) => {
    const next = { ...(value || {}) };
    if (nextStr === "") {
      delete next[code];
    } else {
      const n = Number(nextStr);
      if (Number.isInteger(n)) next[code] = n;
    }
    onChange(next);
  };

  const addPair = () => {
    const n = Number(addVal);
    if (!addKey || !Number.isInteger(n)) return;
    onChange({ ...(value || {}), [addKey]: n });
    // reset + focus the first field for fast data entry
    setAddKey("");
    setAddVal("");
    // small timeout lets MUI commit state before focusing
    setTimeout(() => keySelectRef.current?.focus(), 0);
  };

  const handleAddKeyDown = (e) => {
    if (e.key === "Enter" && isAddValid) {
      e.preventDefault();
      addPair();
    }
  };

  return (
    <div>
      {title && (
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          {title}
        </Typography>
      )}

      {/* Existing entries */}
      {Object.entries(value || {})
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([code, val]) => (
          <div
            key={code}
            style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8 }}
          >
            <TextField
              label="Room Code"
              value={code}
              size={size}
              InputProps={{ readOnly: true }}
              sx={{ width: 180 }}
            />
            <TextField
              label="Amount"
              type="number"
              size={size}
              sx={{ width: 200 }}
              value={Number.isInteger(val) ? val : ""}
              onChange={(e) => handleValueChange(code, e.target.value)}
            />
            <Button
              variant="outlined"
              color="error"
              size={size}
              onClick={() => handleValueChange(code, "")}
            >
              Remove
            </Button>
          </div>
        ))}

      {/* Add new entry */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12 }}>
        <FormControl sx={{ width: 220 }} size={size}>
          <InputLabel id="add-pre-key-label">Add Code</InputLabel>
          <Select
            labelId="add-pre-key-label"
            label="Add Code"
            value={addKey}
            onChange={(e) => setAddKey(e.target.value)}
            onKeyDown={handleAddKeyDown}
            // focus target after adding
            inputRef={keySelectRef}
          >
            {availableKeys.map((k) => (
              <MenuItem key={k} value={k}>
                {k}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Required Availability Count"
          type="number"
          size={size}
          sx={{ width: 200 }}
          value={addVal}
          onChange={(e) => setAddVal(e.target.value)}
          onKeyDown={handleAddKeyDown}
        />

        <Button
          variant="contained"
          size={size}
          onClick={addPair}
          disabled={!isAddValid}
        >
          Add
        </Button>
      </div>
    </div>
  );
}
